import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Baby, 
  Calendar, 
  Activity, 
  FileText, 
  Users, 
  Clock, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { auth } from '../services/api';

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Get current user data (includes children)
      const response = await auth.getCurrentUser();
      const userData = response.data.data;
      
      setCurrentUser(userData);
      setChildren(userData.children || []);

    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Falha ao carregar dados do usuário');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getRelationshipText = (relationship) => {
    switch (relationship) {
      case 'mother':
        return 'Mãe';
      case 'father':
        return 'Pai';
      case 'guardian':
        return 'Responsável';
      case 'grandmother':
        return 'Avó';
      case 'grandfather':
        return 'Avô';
      default:
        return relationship;
    }
  };

  const handleViewChild = (childId) => {
    navigate(`/parent/children/${childId}`);
  };

  const handleViewSessions = (childId) => {
    navigate(`/parent/children/${childId}/sessions`);
  };

  const handleViewBehaviors = (childId) => {
    navigate(`/parent/children/${childId}/behaviors`);
  };

  const handleViewTreatmentPlan = (childId) => {
    navigate(`/parent/children/${childId}/treatment-plan`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-800 border border-red-200 rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-brand-700 to-brand-800 rounded-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Bem-vindo(a), {currentUser?.firstName}!
            </h1>
            <p className="text-brand-100">
              Acompanhe o progresso de suas crianças
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-brand-200">Crianças vinculadas</div>
            <div className="text-3xl font-bold">{children.length}</div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-brand-100 rounded-lg">
              <Calendar className="w-5 h-5 text-brand-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Próximas sessões</p>
              <p className="text-lg font-semibold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Comportamentos</p>
              <p className="text-lg font-semibold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Planos de tratamento</p>
              <p className="text-lg font-semibold text-gray-900">0</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Progresso geral</p>
              <p className="text-lg font-semibold text-gray-900">-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Children Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Baby className="w-5 h-5" />
            Suas Crianças ({children.length})
          </h2>
        </div>

        {children.length === 0 ? (
          <div className="p-8 text-center">
            <Baby className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma criança vinculada</h3>
            <p className="text-gray-600">
              Entre em contato com a administração para vincular suas crianças
            </p>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child) => (
                <div key={child.assignmentId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center">
                        <Baby className="w-6 h-6 text-brand-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-gray-900">
                          {child.patient?.firstName} {child.patient?.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {calculateAge(child.patient?.dateOfBirth)} anos
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-brand-100 text-brand-800">
                        {getRelationshipText(child.relationship)}
                      </span>
                      {child.isPrimary && (
                        <div className="mt-1">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Principal
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleViewChild(child.patient.id)}
                      className="w-full text-left p-2 text-sm text-gray-700 hover:bg-white rounded flex items-center justify-between"
                    >
                      <span>Ver detalhes</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleViewSessions(child.patient.id)}
                      className="w-full text-left p-2 text-sm text-gray-700 hover:bg-white rounded flex items-center justify-between"
                    >
                      <span>Sessões</span>
                      <Calendar className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleViewBehaviors(child.patient.id)}
                      className="w-full text-left p-2 text-sm text-gray-700 hover:bg-white rounded flex items-center justify-between"
                    >
                      <span>Comportamentos</span>
                      <Activity className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleViewTreatmentPlan(child.patient.id)}
                      className="w-full text-left p-2 text-sm text-gray-700 hover:bg-white rounded flex items-center justify-between"
                    >
                      <span>Plano de tratamento</span>
                      <FileText className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Status Indicators */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
                        <span>Ativo</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 text-gray-400 mr-1" />
                        <span>Última sessão: -</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Atividade Recente
          </h2>
        </div>
        <div className="p-6">
          <div className="text-center text-gray-500 py-8">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>Nenhuma atividade recente</p>
            <p className="text-sm">As atividades aparecerão aqui conforme o progresso</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard; 